import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  IconButton,
  InputAdornment,
  Grid,
  Typography,
  Box,
  Divider,
  Button as MuiButton,
} from '@mui/material';
import { orderBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

/* eslint-disable pii/no-phone-number */

import projectPackage from '../../../package.json';
import { Input, Button } from '../../components';

import { DASHBOARD, QUITAR, SIGNUP } from '../../routes';
import { LoginResponse } from '../../services';
import {
  useDispatch,
  fetchLogin,
  useSelector,
  profileSlice,
  vehiclesSlice,
  paymentMethodsSlice,
  ticketPurchaseSlice,
  fetchPasswordRecovery,
  fetchComplianceForSignup,
} from '../../stores';

import { palette, logo } from '../../theme';
import { handleApiReturn, handleErrors } from '../../utils';
import { AppStoreStoreButton, GooglePlayStoreButton } from './StoreButtons';
import {
  StyledWrapper,
  StyledBodyContainer,
  StyledBodyBox,
  StyledLogo,
  StyledLogoWrapper,
  StyledButtonsBox,
  StyledFormGroup,
} from './styles';

import { validationSchema, validationRecoverySchema } from './validations';

export const Login: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { authLoading } = useSelector((state) => state.authReducer);

  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [isRecovering, setIsRecovering] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (
      localStorage.getItem('NITROT:driverId') !== null &&
      localStorage.getItem('NITROT:token') !== null
    ) {
      navigate(DASHBOARD);
    } else {
      dispatch(fetchComplianceForSignup());
    }
  }, []);

  const handleSubmit = () => {
    setErrors({});
    validationSchema
      .validate(formData, { abortEarly: false })
      .then(() =>
        dispatch(
          fetchLogin({
            email: formData.email,
            password: formData.password,
          }),
        ).then((res: { payload: LoginResponse }) => {
          handleApiReturn(
            res,
            () => {
              dispatch(
                profileSlice.actions.setUser({
                  driverId: res.payload.data.id,
                  name: res.payload.data.name,
                  cellphone: res.payload.data.cellphone,
                  email: res.payload.data.email,
                  birth: res.payload.data.birthDate,
                  firstTicket: res.payload.data.firstTicket,
                  picture: res.payload.data.picture || '',
                  termsOfUseDateTime: res.payload.data.termsOfUseDateTime,
                  passwordUpdateDateTime:
                    res.payload.data.passwordUpdateDateTime,
                }),
              );
              dispatch(
                vehiclesSlice.actions.setVehicles(
                  orderBy(res.payload.data.vehicles, ['default'], ['desc']),
                ),
              );
              dispatch(
                paymentMethodsSlice.actions.setPaymentMethods(
                  res.payload.data.paymentMethods,
                ),
              );
              dispatch(
                ticketPurchaseSlice.actions.setDiscount(
                  res.payload.data.discount,
                ),
              );
              localStorage.setItem('NITROT:token', res.payload.data.token);
              localStorage.setItem(
                'NITROT:driverId',
                res.payload.data.id.toString(),
              );
              window.dispatchEvent(new Event('storage'));
              toast.success('Bem vindo!');
              navigate(DASHBOARD);
            },
            () => {},
          );
        }),
      )
      .catch((err) => {
        setErrors(handleErrors(err));
      });
  };

  const handleSignup = () => {
    navigate(SIGNUP);
  };

  const handleForgotPassword = () => {
    setFormData({
      email: '',
      password: '',
    });
    setErrors({});
    setIsRecovering(!isRecovering);
  };

  const handlePasswordRecovery = () => {
    setErrors({});
    validationRecoverySchema
      .validate(formData, { abortEarly: false })
      .then(() =>
        dispatch(
          fetchPasswordRecovery({
            email: formData.email,
          }),
        ).then((res: { payload: LoginResponse }) => {
          handleApiReturn(
            res,
            () => {
              setIsRecovering(false);
              setFormData({
                email: '',
                password: '',
              });
              toast.success('Email enviado com sucesso!');
            },
            null,
          );
        }),
      )
      .catch((err) => {
        setErrors(handleErrors(err));
      });
  };

  return (
    <StyledWrapper
      sx={{
        overflow: 'auto',
        paddingTop: {
          xs: 0,
          md: '60px',
        },
        paddingBottom: {
          xs: 0,
          md: '60px',
        },
      }}
    >
      <Box
        boxShadow={20}
        sx={{
          width: {
            xs: '100%',
            md: 'auto',
          },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'stretch',
          marginTop: {
            xs: '10px',
            md: '10px',
          },
          borderRadius: {
            xs: 0,
            md: '14px',
          },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
            pb: 1,
          }}
        >
          <StyledLogoWrapper>
            <StyledLogo src={logo} alt='logo' />
          </StyledLogoWrapper>
          <StyledBodyContainer>
            {!isRecovering ? (
              <StyledBodyBox>
                <StyledFormGroup>
                  <Input
                    data-testid='email'
                    type='text'
                    margin='dense'
                    label='Email'
                    value={formData.email}
                    onChange={(value) =>
                      setFormData({ ...formData, email: value })
                    }
                    errorMessage={errors.email}
                  />
                  <Input
                    data-testid='password'
                    type={showPassword ? 'text' : 'password'}
                    margin='dense'
                    label='Senha'
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={() => setShowPassword(!showPassword)}
                            onMouseDown={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    value={formData.password}
                    onChange={(InputProps) =>
                      setFormData({ ...formData, password: InputProps })
                    }
                    errorMessage={errors && errors.password}
                  />
                  <StyledButtonsBox>
                    <Button
                      data-testid='submit'
                      variant='contained'
                      label='Entrar'
                      loading={authLoading}
                      style={{ width: '100%' }}
                      onClick={handleSubmit}
                      color='secondary'
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mt: 1.5,
                        gap: 1,
                      }}
                    >
                      <MuiButton
                        variant='text'
                        color='primary'
                        onClick={handleSignup}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 0.5,
                          minWidth: 0,
                          textDecoration: 'underline',
                          textUnderlineOffset: 3,
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Criar conta
                      </MuiButton>
                      <MuiButton
                        variant='text'
                        color='primary'
                        onClick={handleForgotPassword}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 0.5,
                          minWidth: 0,
                          textDecoration: 'underline',
                          textUnderlineOffset: 3,
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Esqueci a senha
                      </MuiButton>
                    </Box>
                  </StyledButtonsBox>
                </StyledFormGroup>
              </StyledBodyBox>
            ) : (
              <StyledBodyBox>
                <StyledFormGroup>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      color='primary'
                      textAlign='center'
                      variant='h4'
                      gutterBottom
                    >
                      Esqueceu sua senha?
                    </Typography>
                    <Typography
                      color={palette.text.main}
                      textAlign='center'
                      gutterBottom
                    >
                      Digite seu e-mail para enviarmos uma nova senha para você
                    </Typography>
                  </Grid>
                  <Input
                    data-testid='email'
                    type='text'
                    margin='normal'
                    label='Email'
                    value={formData.email}
                    onChange={(value) =>
                      setFormData({ ...formData, email: value })
                    }
                    errorMessage={errors.email}
                  />
                  <StyledButtonsBox>
                    <Button
                      data-testid='submit'
                      variant='contained'
                      label='Recuperar senha'
                      loading={authLoading}
                      style={{ width: '100%' }}
                      onClick={handlePasswordRecovery}
                      color='secondary'
                    />
                    <MuiButton
                      variant='text'
                      color='primary'
                      onClick={handleForgotPassword}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        mt: 1.5,
                        textDecoration: 'underline',
                        textUnderlineOffset: 3,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Voltar
                    </MuiButton>
                  </StyledButtonsBox>
                </StyledFormGroup>
              </StyledBodyBox>
            )}
          </StyledBodyContainer>
        </Box>

        <Box
          sx={{
            backgroundColor: '#f3f3f3',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            pt: 2,
            pb: 4,
            px: 2,
            boxSizing: 'border-box',
          }}
        >
          {!isRecovering && (
            <StyledBodyContainer>
              <Divider sx={{ width: '100%', mb: 1.5 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{
                    px: 1,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  Sem cadastro
                </Typography>
              </Divider>
              <MuiButton
                variant='contained'
                color='primary'
                onClick={() => navigate(QUITAR)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  width: '100%',
                  mb: 2,
                }}
              >
                Quitar irregularidades
              </MuiButton>
            </StyledBodyContainer>
          )}

          {(!!process.env.REACT_APP_GOOGLE_PLAY_LINK ||
            !!process.env.REACT_APP_APPLE_STORE_LINK) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                maxWidth: 475,
                boxSizing: 'border-box',
              }}
            >
              <Divider sx={{ width: '100%', mb: 0.5 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{
                    px: 1,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  Disponível também no app
                </Typography>
              </Divider>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 1.25,
                  width: '100%',
                }}
              >
                {!!process.env.REACT_APP_GOOGLE_PLAY_LINK && (
                  <GooglePlayStoreButton
                    url={process.env.REACT_APP_GOOGLE_PLAY_LINK}
                    height={44}
                  />
                )}
                {!!process.env.REACT_APP_APPLE_STORE_LINK && (
                  <AppStoreStoreButton
                    url={process.env.REACT_APP_APPLE_STORE_LINK}
                    height={44}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            py: 1,
            position: 'relative',
            zIndex: 1,
            boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Typography color={palette.text.main} fontSize='12px'>
            Versão {projectPackage.version}
          </Typography>
        </Box>
      </Box>
      {/* <Grid
        xs={12}
        sx={{
          display: {
            xs: 'none',
            sm: 'flex',
          },
          justifyContent: 'center',
        }}
      >
        <Typography padding={5} color={palette.primary.contrastText}>
          Versão {projectPackage.version}
        </Typography>
      </Grid> */}
    </StyledWrapper>
  );
};
